import { type EventRecord, SEVERITIES, type ZoneSummary } from '@opensentry/shared';
import maplibregl from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { SEVERITY_COLORS, SEVERITY_ICONS } from './SeverityPill.js';

// Carto "Dark Matter" raster basemap — desaturated, no token (§2, §12).
const STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap, © CARTO',
    },
  },
  layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
};

// MapLibre paints on a WebGL canvas and CANNOT resolve CSS variables, so
// the map must use literal hex (not SEVERITY_COLORS, which are var(...)).
// These mirror --color-sev-* in globals.css. Calm = bright neutral so
// "no active threat" zones stay clearly visible on the dark basemap.
const SEV_HEX = {
  low: '#3f8f5e',
  medium: '#c9a227',
  high: '#d97b29',
  critical: '#e23636',
} as const;
const CALM = '#c2c8d2';
const sevColor = (s: string | null) =>
  s ? (SEV_HEX[s as keyof typeof SEV_HEX] ?? CALM) : CALM;

const SEV_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, critical: 3 };
// Cluster fill keyed off the max event severity inside it.
const CLUSTER_COLOR: maplibregl.ExpressionSpecification = [
  'step',
  ['get', 'maxSev'],
  SEV_HEX.low,
  1,
  SEV_HEX.medium,
  2,
  SEV_HEX.high,
  3,
  SEV_HEX.critical,
];

/** Synthesize a ~radiusKm circle ring so a zone is always drawable even
 *  if the API hasn't sent its H3 boundary (stale deploy / older worker). */
function circleRing(lng: number, lat: number, radiusKm: number): [number, number][] {
  const ring: [number, number][] = [];
  const dLat = radiusKm / 111;
  const dLng = radiusKm / (111 * Math.max(0.05, Math.cos((lat * Math.PI) / 180)));
  for (let i = 0; i <= 32; i++) {
    const a = (i / 32) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return ring;
}

export function MapView({
  zones,
  events,
  onZoneClick,
  onEventClick,
}: {
  zones: ZoneSummary[];
  events: EventRecord[];
  onZoneClick: (id: number) => void;
  onEventClick: (id: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time init
  useEffect(() => {
    if (!ref.current || map.current) return;
    const m = new maplibregl.Map({
      container: ref.current,
      style: STYLE,
      center: [10, 25],
      zoom: 1.6,
      attributionControl: { compact: true },
    });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-left');

    m.on('load', () => {
      m.addSource('events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 45,
        clusterProperties: { maxSev: ['max', ['get', 'sevRank']] },
      });
      m.addSource('zones', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      m.addLayer({
        id: 'zone-fill',
        type: 'fill',
        source: 'zones',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': ['case', ['get', 'calm'], 0.1, 0.32],
        },
      });
      m.addLayer({
        id: 'zone-outline',
        type: 'line',
        source: 'zones',
        paint: {
          'line-color': ['get', 'color'],
          // Every zone gets a bold, FULLY OPAQUE ring so it's always
          // clearly visible; alarmed zones are thicker and severity-colored.
          'line-width': ['case', ['get', 'calm'], 2.5, 4.5],
          'line-opacity': 1,
        },
      });
      m.addLayer({
        id: 'zone-labels',
        type: 'symbol',
        source: 'zones',
        layout: { 'text-field': ['get', 'label'], 'text-size': 10 },
        paint: { 'text-color': '#cfd4dc', 'text-halo-color': '#0b0d10', 'text-halo-width': 1 },
      });

      m.addLayer({
        id: 'event-clusters',
        type: 'circle',
        source: 'events',
        filter: ['has', 'point_count'],
        paint: {
          // Fill (not just the ring) carries the worst severity in the
          // cluster, so it's unmistakable at a glance.
          'circle-color': CLUSTER_COLOR,
          'circle-opacity': 0.85,
          'circle-stroke-color': '#0b0d10',
          'circle-stroke-width': 1.5,
          'circle-radius': ['step', ['get', 'point_count'], 13, 10, 18, 50, 24],
        },
      });
      m.addLayer({
        id: 'event-cluster-count',
        type: 'symbol',
        source: 'events',
        filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 12 },
        paint: {
          'text-color': '#0b0d10',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.2,
        },
      });
      m.addLayer({
        id: 'event-points',
        type: 'circle',
        source: 'events',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 6,
          'circle-color': ['get', 'color'],
          // Events are context, not the headline — keep them faint so the
          // workforce zones read first.
          'circle-opacity': 0.22,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-opacity': 0.5,
          'circle-stroke-width': 1,
        },
      });

      m.on('click', 'zone-fill', (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id != null) onZoneClick(Number(id));
      });
      m.on('click', 'event-points', (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id != null) onEventClick(Number(id));
      });
      // Click a cluster → zoom to the level where it breaks apart.
      m.on('click', 'event-clusters', async (e) => {
        const f = e.features?.[0];
        const clusterId = f?.properties?.cluster_id;
        if (f == null || clusterId == null) return;
        const src = m.getSource('events') as maplibregl.GeoJSONSource;
        const zoom = await src.getClusterExpansionZoom(Number(clusterId));
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        if (lng == null || lat == null) return;
        m.easeTo({ center: [lng, lat], zoom });
      });
      for (const layer of ['event-clusters', 'zone-fill', 'event-points']) {
        m.on('mouseenter', layer, () => {
          m.getCanvas().style.cursor = 'pointer';
        });
        m.on('mouseleave', layer, () => {
          m.getCanvas().style.cursor = 'default';
        });
      }
    });

    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const apply = () => {
      const zsrc = m.getSource('zones') as maplibregl.GeoJSONSource | undefined;
      const esrc = m.getSource('events') as maplibregl.GeoJSONSource | undefined;
      zsrc?.setData({
        type: 'FeatureCollection',
        features: zones.map((z) => ({
          // Draw the zone's actual exposure radius (the area within which
          // an event threatens it) — bigger and more meaningful than the
          // small H3 cell, and independent of the API boundary field.
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [circleRing(z.centroidLng, z.centroidLat, z.radiusKm || 150)],
          },
          properties: {
            id: z.id,
            label: z.label ?? `Zone ${z.id}`,
            color: sevColor(z.maxSeverity),
            calm: z.maxSeverity == null,
          },
        })),
      });
      esrc?.setData({
        type: 'FeatureCollection',
        features: events.map((ev) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [ev.lng, ev.lat] },
          properties: {
            id: ev.id,
            color: sevColor(ev.severity),
            sevRank: SEV_RANK[ev.severity] ?? 0,
          },
        })),
      });
    };
    if (m.isStyleLoaded() && m.getSource('zones')) apply();
    else m.once('idle', apply);
  }, [zones, events]);

  return (
    <div className="relative h-full w-full">
      <div ref={ref} className="h-full w-full" />
      <div className="absolute right-3 top-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]/90 px-3 py-2 text-[11px] shadow-lg backdrop-blur">
        <div className="mb-1.5 font-semibold tracking-tight text-[var(--color-muted)]">
          Severity
        </div>
        <div className="flex flex-col gap-1">
          {SEVERITIES.map((s) => {
            const Icon = SEVERITY_ICONS[s];
            return (
              <div key={s} className="flex items-center gap-1.5">
                <Icon size={12} style={{ color: SEVERITY_COLORS[s] }} aria-hidden />
                <span className="capitalize">{s}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border border-[var(--color-border)]"
              style={{ background: CALM }}
            />
            <span>No active threat</span>
          </div>
        </div>
      </div>
    </div>
  );
}
